export interface HoldableSubscription {
    status?: string | null
    paymentsOnHold?: boolean | null
    paymentsHoldUntil?: string | null
    frequency?: string | null
    externalSubscriptionId?: string | null
}

export interface PaymentsHold {
    onHold: boolean
    /** True when some subscriptions in force are not on hold, so payments are suspended only in part. */
    partial: boolean
    /** When every held subscription has an end date, the earliest one (the first to start billing); otherwise null. */
    until: string | null
}

// A subscription that is over has no payments left to hold
const ENDED_STATUSES = new Set(['canceled', 'incomplete_expired'])

/**
 * Whether payments for a license are on hold: any subscription still in force that the backend reports as held.
 */
export const getPaymentsHold = (subscriptions: HoldableSubscription[] | null | undefined): PaymentsHold => {
    // A one-time purchase has no recurring payments to hold, so it does not make a hold partial
    const recurring = (subscription: HoldableSubscription) =>
        subscription.frequency !== 'one_time' && subscription.externalSubscriptionId !== null
    const inForce = (subscriptions ?? []).filter(
        (subscription) => !ENDED_STATUSES.has(String(subscription.status ?? '')) && recurring(subscription),
    )
    const held = inForce.filter((subscription) => subscription.paymentsOnHold)
    if (!held.length) return { onHold: false, partial: false, until: null }

    const partial = held.length < inForce.length
    const ends = held.map((subscription) => subscription.paymentsHoldUntil)
    // One subscription held without an end date means it stays suspended until someone releases it
    if (ends.some((end) => !end)) return { onHold: true, partial, until: null }

    const earliest = ends.reduce((a, b) => (new Date(a as string) < new Date(b as string) ? a : b))
    return { onHold: true, partial, until: earliest as string }
}
